import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import Swal from "sweetalert2";
import { CalendarDays, CheckCircle2, KeyRound, RefreshCw, ShieldCheck } from "lucide-react";
import { api, url } from "../../axios/api";

const LicenseTab = () => {
  const user = useSelector((state) => state.user.user);
  const empresaId = user?.empresa_id;
  const today = new Date().toISOString().slice(0, 10);

  const [codigo, setCodigo] = useState("");
  const [expireAt, setExpireAt] = useState("");
  const [status, setStatus] = useState(null);
  const [validated, setValidated] = useState(false);
  const [loading, setLoading] = useState(false);

  const checkLicense = async () => {
    if (!empresaId) return;
    setLoading(true);
    try {
      const response = await api.get(`/auth/license/active/${empresaId}`);
      setStatus(response.data);
    } catch (error) {
      setStatus({ ativo: false });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    checkLicense();
  }, [empresaId]);

  const validateCode = async () => {
    setLoading(true);
    try {
      const response = await api.post("/auth/license/validate-code", { codigo });
      const valid = response.data.valid || response.data.habilitado;
      setValidated(valid);
      if (!valid) throw new Error(response.data.message || "Codigo invalido.");
      Swal.fire("Codigo validado", "Defina o prazo e ative a licenca.", "success");
    } catch (error) {
      setValidated(false);
      Swal.fire("Erro", error.response?.data?.error || error.message || "Codigo invalido.", "error");
    } finally {
      setLoading(false);
    }
  };

  const activateLicense = async (event) => {
    event.preventDefault();
    if (!validated) return Swal.fire("Atencao", "Valide primeiro o codigo da licenca.", "warning");
    if (!expireAt) return Swal.fire("Atencao", "Selecione a data de expiracao.", "warning");

    setLoading(true);
    try {
      const response = await api.post("/auth/license/activate", {
        empresa_id: empresaId,
        codigo,
        createdAt: today,
        expireAt,
      });

      if (response.data.fileName) {
        window.open(`${url}/mediafiles/${response.data.fileName}`, "_blank", "noopener,noreferrer");
      }

      await checkLicense();
      Swal.fire("Licenca ativa", "Licenca ativada de acordo com o prazo definido.", "success");
    } catch (error) {
      Swal.fire("Erro", error.response?.data?.error || "Falha ao ativar licenca.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="license-panel">
      <div className="license-status">
        <div className={status?.ativo ? "status-icon active" : "status-icon"}>
          <ShieldCheck size={28} />
        </div>
        <div>
          <span className="license-eyebrow">Estado da licenca</span>
          <h3>{status?.ativo ? "Ativa" : "Inativa ou expirada"}</h3>
          <p>{status?.expires_at ? `Valida ate ${status.expires_at}` : "Sem prazo ativo encontrado."}</p>
        </div>
        <button type="button" className="win10-btn win10-btn-outline" onClick={checkLicense} disabled={loading}>
          <RefreshCw size={15} /> Verificar
        </button>
      </div>

      <form className="license-form" onSubmit={activateLicense}>
        <div className="field">
          <label>Codigo da licenca</label>
          <div className="input-row">
            <KeyRound size={17} />
            <input
              value={codigo}
              onChange={(event) => {
                setCodigo(event.target.value);
                setValidated(false);
              }}
              placeholder="facturize_saft_25"
              required
            />
            <button type="button" onClick={validateCode} disabled={loading || !codigo}>
              {validated ? <CheckCircle2 size={16} /> : "Validar"}
            </button>
          </div>
        </div>

        <div className="field">
          <label>Prazo de ativacao</label>
          <div className="input-row">
            <CalendarDays size={17} />
            <input type="date" min={today} value={expireAt} onChange={(event) => setExpireAt(event.target.value)} disabled={!validated} required />
          </div>
        </div>

        <button className="win10-btn win10-btn-primary" type="submit" disabled={loading || !validated}>
          Ativar licenca
        </button>
      </form>

      <style>{`
        .license-panel { display: grid; gap: 16px; }
        .license-status { display: grid; grid-template-columns: auto 1fr auto; gap: 14px; align-items: center; border: 1px solid #d2d0ce; background: #fff; padding: 16px; }
        .status-icon { width: 54px; height: 54px; display: grid; place-items: center; background: #f3f2f1; color: #8a8886; }
        .status-icon.active { background: #e6f4ea; color: #107c10; }
        .license-eyebrow { display: block; font-size: 11px; font-weight: 800; color: #605e5c; text-transform: uppercase; }
        .license-status h3 { margin: 2px 0; font-size: 22px; font-weight: 800; }
        .license-status p { margin: 0; color: #605e5c; font-size: 13px; }
        .license-form { border: 1px solid #d2d0ce; background: #fff; padding: 16px; display: grid; gap: 14px; max-width: 720px; }
        .field label { display: block; font-size: 11px; font-weight: 800; color: #605e5c; text-transform: uppercase; margin-bottom: 5px; }
        .input-row { display: flex; align-items: center; gap: 8px; border: 1px solid #8a8886; padding: 0 8px; min-height: 38px; background: #fff; }
        .input-row input { flex: 1; border: 0; outline: 0; min-height: 34px; font-size: 14px; }
        .input-row button { border: 0; background: #0078d4; color: #fff; min-height: 28px; padding: 0 12px; font-weight: 700; }
        .win10-btn { border-radius: 0; border: 1px solid transparent; min-height: 36px; padding: 0 14px; display: inline-flex; align-items: center; justify-content: center; gap: 7px; font-weight: 700; }
        .win10-btn-primary { background: #0078d4; color: #fff; }
        .win10-btn-outline { background: #fff; color: #323130; border-color: #8a8886; }
        @media (max-width: 700px) { .license-status { grid-template-columns: 1fr; } }
      `}</style>
    </div>
  );
};

export default LicenseTab;
